/**
 * @fileoverview Application State Container
 * Minimal state management for CAP.IO without external dependencies.
 * @module core/state
 *
 * CONTRACT:
 * - createAppState() returns a state instance (not a singleton)
 * - State is encapsulated in a closure; no global mutable state
 * - Callers (e.g., main.js) own and manage the instance
 * - State changes can be subscribed to via onChange callback
 *
 * STATE SHAPE:
 * - route: { page: string, id?: string }
 * - timeRange: string (e.g., "1Y", "6M")
 */

/**
 * Route object representing the current navigation state.
 * @typedef {Object} Route
 * @property {string} page - The page identifier
 * @property {string} [id] - Optional resource identifier
 */

/**
 * Application state shape.
 * @typedef {Object} AppState
 * @property {Route} route - Current route
 * @property {string} timeRange - Current time range for data views
 */

/**
 * State change listener callback.
 * @callback StateChangeListener
 * @param {AppState} state - The new state
 * @param {string} changedKey - The key that changed ("route" or "timeRange")
 */

/**
 * Application state container interface.
 * @typedef {Object} AppStateContainer
 * @property {function(): Route} getRoute - Get current route
 * @property {function(Route): void} setRoute - Set current route
 * @property {function(): string} getTimeRange - Get current time range
 * @property {function(string): void} setTimeRange - Set current time range
 * @property {function(): AppState} getState - Get full state snapshot
 * @property {function(StateChangeListener): function(): void} subscribe - Subscribe to state changes
 */

/**
 * Default time range aligned with DLO manifest.
 * @type {string}
 */
const DEFAULT_TIME_RANGE = "1Y";

/**
 * Creates an application state container.
 * This is a factory function that returns a new state instance.
 * State is encapsulated in a closure to prevent external mutation.
 *
 * @param {Route} initialRoute - Initial route state
 * @param {string} [initialTimeRange] - Initial time range (defaults to "1Y")
 * @returns {AppStateContainer} State container with getter/setter methods
 *
 * @example
 * const state = createAppState({ page: "home" });
 * state.setRoute({ page: "observatories" });
 * console.log(state.getRoute()); // { page: "observatories" }
 *
 * const unsubscribe = state.subscribe((newState, changedKey) => {
 *   console.log(`${changedKey} changed:`, newState);
 * });
 */
export function createAppState(initialRoute, initialTimeRange = DEFAULT_TIME_RANGE) {
  // Private state encapsulated in closure
  let route = { ...initialRoute };
  let timeRange = initialTimeRange;

  /** @type {Set<StateChangeListener>} */
  const listeners = new Set();

  /**
   * Notifies all listeners of a state change.
   * @param {string} changedKey - The key that changed
   */
  function notifyListeners(changedKey) {
    const snapshot = { route: { ...route }, timeRange };
    for (const listener of listeners) {
      try {
        listener(snapshot, changedKey);
      } catch (error) {
        console.error("[AppState] Listener error:", error);
      }
    }
  }

  return {
    /**
     * Gets the current route.
     * @returns {Route} Copy of current route
     */
    getRoute() {
      return { ...route };
    },

    /**
     * Sets the current route and notifies listeners.
     * @param {Route} newRoute - New route to set
     */
    setRoute(newRoute) {
      route = { ...newRoute };
      notifyListeners("route");
    },

    /**
     * Gets the current time range.
     * @returns {string} Current time range
     */
    getTimeRange() {
      return timeRange;
    },

    /**
     * Sets the time range and notifies listeners.
     * @param {string} newTimeRange - New time range to set
     */
    setTimeRange(newTimeRange) {
      timeRange = newTimeRange;
      notifyListeners("timeRange");
    },

    /**
     * Gets a snapshot of the full state.
     * @returns {AppState} Copy of current state
     */
    getState() {
      return {
        route: { ...route },
        timeRange,
      };
    },

    /**
     * Subscribes to state changes.
     * @param {StateChangeListener} listener - Callback for state changes
     * @returns {function(): void} Unsubscribe function
     */
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
